import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Select,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { useLiveopsChange } from '@/hooks/useLiveopsChange';
import { adminApi } from '@/services/api';
import { apiErrorMessage } from '@/utils/apiError';
import { countriesWithoutTeamsWarning } from '@/utils/cricketCalendar';
import type { CricketCalendarSettings } from '@/types/matchPushes';

const { Text } = Typography;
const ISO2 = /^[A-Z]{2}$/;

interface TrackingForm {
  trackedCountries: string[];
  autoTrackLeagues: string[];
}

interface Props {
  settings: CricketCalendarSettings | null;
  onSaved: (settings: CricketCalendarSettings) => void;
}

/**
 * Which matches are tracked automatically: every match played to a tracked
 * country, plus any series whose name contains one of the leagues. A match an
 * admin switched on or off by hand keeps that choice.
 */
export const CalendarSettingsCard: React.FC<Props> = ({ settings, onSaved }) => {
  const [form] = Form.useForm<TrackingForm>();
  const [saving, setSaving] = useState(false);
  const { canChange, deniedTitle } = useLiveopsChange();

  useEffect(() => {
    if (settings) {
      form.setFieldsValue({
        trackedCountries: settings.trackedCountries,
        autoTrackLeagues: settings.autoTrackLeagues,
      });
    }
  }, [settings, form]);

  const save = async (values: TrackingForm) => {
    setSaving(true);
    try {
      const response = await adminApi.cricketCalendar.updateSettings({
        trackedCountries: values.trackedCountries ?? [],
        autoTrackLeagues: (values.autoTrackLeagues ?? []).map((l) => l.trim()).filter(Boolean),
      });
      message.success('Settings saved; tracking re-applied to upcoming matches');
      onSaved(response.data);
    } catch (error) {
      message.error(apiErrorMessage(error, 'Failed to save the settings'));
    } finally {
      setSaving(false);
    }
  };

  const confirmSave = async () => {
    let values: TrackingForm;
    try {
      values = await form.validateFields();
    } catch {
      return; // the form shows which fields to fix
    }
    Modal.confirm({
      title: 'Save the tracking settings?',
      content:
        'Re-applies tracking to upcoming matches: the ones this rule tracks change with the countries and leagues, and their pushes are planned or cancelled to match. Matches an admin switched on or off by hand keep that choice.',
      okText: 'Save and re-apply',
      onOk: () => save(values),
    });
  };

  const noTeams = countriesWithoutTeamsWarning(settings?.countriesWithoutTeams ?? []);

  return (
    <Card size="small" title="Settings: what gets tracked" loading={!settings}>
      {noTeams && <Alert type="warning" showIcon message={noTeams} style={{ marginBottom: 12 }} />}
      <Form form={form} layout="vertical" disabled={!canChange}>
        <Row gutter={16}>
          <Col xs={24} md={10}>
            <Form.Item
              name="trackedCountries"
              label="Tracked countries"
              extra="Matches played to players in these countries are tracked."
              normalize={(value: string[] | undefined) =>
                (value ?? []).map((c) => c.trim().toUpperCase())
              }
              rules={[
                {
                  validator: (_, value: string[] | undefined) =>
                    (value ?? []).every((c) => ISO2.test(c))
                      ? Promise.resolve()
                      : Promise.reject(new Error('Use two-letter country codes')),
                },
              ]}
            >
              <Select mode="tags" tokenSeparators={[',', ' ']} open={false} placeholder="IN" />
            </Form.Item>
          </Col>
          <Col xs={24} md={14}>
            <Form.Item
              name="autoTrackLeagues"
              label="Auto-track leagues"
              extra="Part of a series name, any case, e.g. Indian Premier League."
            >
              <Select mode="tags" tokenSeparators={[',']} open={false} />
            </Form.Item>
          </Col>
        </Row>
        <Tooltip title={deniedTitle}>
          <Button type="primary" loading={saving} disabled={!canChange} onClick={confirmSave}>
            Save
          </Button>
        </Tooltip>
        <Text type="secondary" style={{ marginLeft: 12 }}>
          Saving re-applies the rule to upcoming matches; hand choices stay.
        </Text>
      </Form>
    </Card>
  );
};
